import React, { useState, useEffect } from "react";
import {
  UserCheck,
  Plus,
  Search,
  Edit,
  Trash2,
  Settings,
  Users,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  getMyOrganizations,
  createOrganization,
  updateOrganization,
  deleteOrganization,
  CreateOrgParams,
  UpdateOrgParams,
  OrgWithPermission,
} from "../services/organization.ts";
import Tooltip from "../components/Tooltip.tsx";
import OrganizationModal from "../components/Modals/OrganizationModal.tsx";
import messageService from "../components/Message/message.ts";

const Organization: React.FC = () => {
  const [organizations, setOrganizations] = useState<OrgWithPermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingOrganization, setEditingOrganization] =
    useState<OrgWithPermission | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<{
    orgId: string;
    orgName: string;
  } | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getMyOrganizations();
      setOrganizations(data);
    } catch (error) {
      console.error("Failed to fetch organizations:", error);
      setError("Failed to load organizations");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    setEditingOrganization(null);
    setShowModal(true);
  };

  const handleOpenEditModal = (org: OrgWithPermission) => {
    setEditingOrganization(org);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingOrganization(null);
    setError(null);
  };

  const handleSubmitOrganization = async (
    data: CreateOrgParams | UpdateOrgParams
  ) => {
    try {
      setModalLoading(true);
      setError(null);

      if (editingOrganization) {
        const response = await updateOrganization(
          editingOrganization.id,
          data as UpdateOrgParams
        );
        console.log("Organization updated:", response);
        messageService.success(
          `Organization "${data.name}" updated successfully!`
        );
      } else {
        const response = await createOrganization(data as CreateOrgParams);
        console.log("Organization created:", response);
        messageService.success(
          `Organization "${response.data.organization.name}" created successfully!`
        );
      }

      handleCloseModal();
      fetchOrganizations();
    } catch (error: any) {
      console.error("Failed to save organization:", error);
      setError(
        error.response?.data?.message ||
          `Failed to ${editingOrganization ? "update" : "create"} organization`
      );
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteOrganization = async (orgId: string, orgName: string) => {
    setShowDeleteConfirm({ orgId, orgName });
  };

  const confirmDelete = async () => {
    if (!showDeleteConfirm) return;

    try {
      setDeleting(showDeleteConfirm.orgId);
      setError(null);
      const response = await deleteOrganization(showDeleteConfirm.orgId);
      console.log("Organization deleted:", response);

      messageService.success(
        `Organization "${showDeleteConfirm.orgName}" deleted successfully!`
      );
      fetchOrganizations();
    } catch (error: any) {
      console.error("Failed to delete organization:", error);
      setError(
        error.response?.data?.message || "Failed to delete organization"
      );
    } finally {
      setDeleting(null);
      setShowDeleteConfirm(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(null);
  };

  const filteredOrganizations = organizations.filter(
    (org) =>
      org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      org.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleViewOrganization = (orgId: string) => {
    navigate(`/organization/${orgId}`);
  };

  return (
    <div className="flex-1 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Customers</h1>
            <p className="text-gray-600 mt-1">
              Manage your customers sites and devices
            </p>
          </div>
          <button
            onClick={handleOpenCreateModal}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Customer
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search customers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
          <div className="mt-2 text-sm text-gray-500 flex items-center">
            <span>💡 Tip: Click any row to open customer details</span>
          </div>
        </div>

        {/* Organizations List */}
        <div className="bg-white rounded-lg shadow-sm border">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading customers...</p>
            </div>
          ) : filteredOrganizations.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No customers found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Owner Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOrganizations.map((org) => (
                    <tr
                      key={org.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleViewOrganization(org.id)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <UserCheck className="w-5 h-5 text-blue-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {org.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              Code: {org.code}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {org.description}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {org.owner?.email || "N/A"}
                        </div>
                        <div className="text-sm text-gray-500">
                          {org.owner
                            ? `${org.owner.firstName} ${org.owner.lastName}`
                            : "Permission-based access"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(org.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <Tooltip
                            content="Configure Customer Sites/Devices..etc"
                            position="topleft"
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewOrganization(org.id);
                              }}
                              className="text-green-600 hover:text-green-900 p-1"
                              aria-label="Configure Customer"
                            >
                              <Settings className="w-5 h-5" />
                            </button>
                          </Tooltip>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenEditModal(org);
                            }}
                            className="text-blue-600 hover:text-blue-900 p-1"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteOrganization(org.id, org.name);
                            }}
                            disabled={deleting === org.id}
                            className={`p-1 ${
                              deleting === org.id
                                ? "text-gray-400 cursor-not-allowed"
                                : "text-red-600 hover:text-red-900"
                            }`}
                            title="Delete"
                          >
                            {deleting === org.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 h-10 w-10">
                  <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                    <Trash2 className="w-5 h-5 text-red-600" />
                  </div>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">
                    Delete Customer
                  </h3>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-sm text-gray-600">
                  Are you sure you want to delete{" "}
                  <strong>"{showDeleteConfirm.orgName}"</strong>? This action
                  cannot be undone.
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={cancelDelete}
                  disabled={deleting === showDeleteConfirm.orgId}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDelete}
                  disabled={deleting === showDeleteConfirm.orgId}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {deleting === showDeleteConfirm.orgId ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Deleting...
                    </div>
                  ) : (
                    "Delete Customer"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Organization Modal */}
        <OrganizationModal
          isOpen={showModal}
          onClose={handleCloseModal}
          onSubmit={handleSubmitOrganization}
          organization={editingOrganization} // Simply pass the editingOrganization directly
          loading={modalLoading}
        />
      </div>
    </div>
  );
};

export default Organization;
